import React, { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Play,
  FileImage,
  FolderPlus,
  UserPlus,
  Pencil,
  Plus,
  Minus,
  Settings,
  Link as LinkIcon,
  Unlink,
  PlayCircle,
  StopCircle,
  Trash2,
  RotateCcw,
  UserMinus,
  Film,
  FilePlus,
  Activity,
  Loader2,
  AlertCircle,
  Bot,
  Building2,
  ShieldPlus,
  ShieldCheck,
  ShieldX,
  KeyRound,
  Undo2,
  Flame,
  Copy,
  Mail,
  PauseCircle,
  Server,
  ExternalLink,
  Globe,
  Shield,
  type LucideIcon,
} from "lucide-react";
import { ScrollArea } from "../ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useActivityFeedInfinite } from "../../hooks/useActivityFeed";
import { useAppStore } from "../../stores/appStore";
import { routes } from "../../routes";
import type {
  ActivityEvent,
  ActivityEventSlug,
  ActivityEventCategory,
} from "../../types/activity.types";
import { toast } from "sonner";
import { getAsset } from "@/services/api/sdk.gen";
import { AssetPreviewModal } from "../assets/AssetPreviewModal";
import type { Asset } from "@/types/asset.types";

// ============================================================
// Detail Chip Types
// ============================================================

interface DetailChip {
  label: string;
  variant?: "default" | "success" | "warning" | "error";
}

const chipVariantClasses: Record<string, string> = {
  default: "bg-muted text-muted-foreground",
  success:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  warning:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

// ============================================================
// Event Configuration
// ============================================================

interface LabelContext {
  workflowLink: (name: string) => React.ReactNode;
  projectLink: (name: string) => React.ReactNode;
  assetLink: (name: string) => React.ReactNode;
  timelineLink: (name: string) => React.ReactNode;
  /** Resolve project name from projectId via the app store */
  getProjectName: (projectId: string | undefined | null) => string | undefined;
}

interface EventConfig {
  icon: LucideIcon;
  color: string;
  getLabel: (event: ActivityEvent, ctx?: LabelContext) => React.ReactNode;
  getDetails?: (event: ActivityEvent) => DetailChip[] | null;
}

/** Strip common prefixes like "workflow-execution-" to get a short human label. */
function humanizeStatus(slug: string): string {
  const short = slug
    .replace(/^workflow-execution-/, "")
    .replace(/^asset-/, "")
    .replace(/^project-/, "")
    .replace(/^workflow-/, "")
    .replace(/-/g, " ");
  return short;
}

function statusTransitionChips(event: ActivityEvent): DetailChip[] | null {
  const oldSlug = event.oldStatus?.slug;
  const newSlug = event.newStatus?.slug;
  if (!newSlug) return null;
  const shortNew = humanizeStatus(newSlug);
  const variant =
    shortNew === "completed" || shortNew === "confirmed"
      ? "success"
      : shortNew === "failed"
        ? "error"
        : shortNew === "cancelled" || shortNew === "pending cancellation"
          ? "warning"
          : "default";
  return [
    {
      label: oldSlug
        ? `${humanizeStatus(oldSlug)} \u2192 ${shortNew}`
        : shortNew,
      variant,
    },
  ];
}

const eventConfig: Partial<Record<ActivityEventSlug, EventConfig>> = {
  "user-created": {
    icon: UserPlus,
    color: "text-green-600",
    getLabel: (e) =>
      `created account ${(e.eventDetails?.user_name as string) || ""}`,
    getDetails: (e) => {
      const email = e.eventDetails?.email as string;
      return email ? [{ label: email }] : null;
    },
  },
  "user-updated": {
    icon: Pencil,
    color: "text-orange-500",
    getLabel: (e) =>
      `updated profile ${(e.eventDetails?.user_name as string) || ""}`,
    getDetails: (e) => {
      const active = e.eventDetails?.active;
      if (active === false) return [{ label: "suspended", variant: "warning" }];
      if (active === true) return [{ label: "active", variant: "success" }];
      return null;
    },
  },
  "user-deleted": {
    icon: UserMinus,
    color: "text-red-600",
    getLabel: (e) =>
      `deleted account ${(e.eventDetails?.user_name as string) || ""}`,
    getDetails: () => [{ label: "soft-deleted", variant: "error" }],
  },
  "team-created": {
    icon: FolderPlus,
    color: "text-blue-600",
    getLabel: (e) =>
      `created team ${(e.eventDetails?.team_name as string) || ""}`,
  },
  "project-created": {
    icon: FolderPlus,
    color: "text-blue-600",
    getLabel: (e, ctx) => {
      const name = (e.eventDetails?.projectName ??
        e.eventDetails?.project_name) as string;
      return (
        <>
          created project{" "}
          {name
            ? (ctx?.projectLink(name) ?? (
                <strong className="text-foreground">{name}</strong>
              ))
            : ""}
        </>
      );
    },
  },
  "workflow-created": {
    icon: Plus,
    color: "text-blue-600",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      return (
        <>
          created workflow{" "}
          {name
            ? (ctx?.workflowLink(name) ?? (
                <strong className="text-foreground">{name}</strong>
              ))
            : ""}
        </>
      );
    },
  },
  "workflow-execution-started": {
    icon: Play,
    color: "text-blue-600",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      const wf = name
        ? (ctx?.workflowLink(name) ?? (
            <strong className="text-foreground">{name}</strong>
          ))
        : "a workflow";
      return <>started {wf}</>;
    },
    getDetails: statusTransitionChips,
  },
  "workflow-execution-completed": {
    icon: CheckCircle2,
    color: "text-green-600",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      const wf = name
        ? (ctx?.workflowLink(name) ?? (
            <strong className="text-foreground">{name}</strong>
          ))
        : "a workflow";
      return <>ran {wf} successfully</>;
    },
    getDetails: statusTransitionChips,
  },
  "workflow-execution-failed": {
    icon: XCircle,
    color: "text-red-600",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      const wf = name
        ? (ctx?.workflowLink(name) ?? (
            <strong className="text-foreground">{name}</strong>
          ))
        : "a workflow";
      return <>{wf} failed</>;
    },
    getDetails: statusTransitionChips,
  },
  "workflow-execution-cancelled": {
    icon: StopCircle,
    color: "text-yellow-600",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      const wf = name
        ? (ctx?.workflowLink(name) ?? (
            <strong className="text-foreground">{name}</strong>
          ))
        : "a workflow";
      return <>cancelled {wf}</>;
    },
    getDetails: statusTransitionChips,
  },
  "workflow-execution-requested": {
    icon: PlayCircle,
    color: "text-blue-400",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      const wf = name
        ? (ctx?.workflowLink(name) ?? (
            <strong className="text-foreground">{name}</strong>
          ))
        : "a workflow";
      return <>requested execution of {wf}</>;
    },
  },
  "workflow-execution-pending-cancellation": {
    icon: StopCircle,
    color: "text-yellow-600",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      const wf = name
        ? (ctx?.workflowLink(name) ?? (
            <strong className="text-foreground">{name}</strong>
          ))
        : "a workflow";
      return <>requested cancellation of {wf}</>;
    },
    getDetails: statusTransitionChips,
  },
  "asset-created": {
    icon: FileImage,
    color: "text-purple-600",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.file_name as string;
      return name ? (
        <span title={name}>
          uploaded {ctx?.assetLink("an asset") ?? <strong>an asset</strong>}
        </span>
      ) : (
        "uploaded an asset"
      );
    },
  },
  "asset-confirmed": {
    icon: CheckCircle2,
    color: "text-green-500",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.file_name as string;
      return name ? (
        <span title={name}>
          uploaded {ctx?.assetLink("an asset") ?? <strong>an asset</strong>}
        </span>
      ) : (
        "uploaded an asset"
      );
    },
  },
  "asset-deleted": {
    icon: Trash2,
    color: "text-red-500",
    getLabel: (e) => {
      const name = e.eventDetails?.file_name as string;
      return name ? (
        <span title={name}>deleted an asset</span>
      ) : (
        "deleted an asset"
      );
    },
  },
  "user-joined-team": {
    icon: UserPlus,
    color: "text-green-600",
    getLabel: (e) => {
      const target = (e.eventDetails?.target_user_name as string) || "a member";
      return `added ${target} to the team`;
    },
    getDetails: (e) => {
      const provider = e.eventDetails?.provider as string;
      return provider ? [{ label: `via ${provider}` }] : null;
    },
  },
  "user-left-team": {
    icon: UserMinus,
    color: "text-red-500",
    getLabel: (e) => {
      const target = (e.eventDetails?.target_user_name as string) || "a member";
      return (e.eventDetails?.removed_by as string) === "self"
        ? `removed ${target} from the team`
        : `removed ${target} from the team`;
    },
    getDetails: (e) => {
      const provider = e.eventDetails?.provider as string;
      return provider ? [{ label: `via ${provider}` }] : null;
    },
  },
  "workflow-metadata-updated": {
    icon: Pencil,
    color: "text-orange-400",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      const d = e.eventDiffs as Record<string, unknown> | null;
      const graphOnly =
        d?.graph_updated === true &&
        !d?.name_updated &&
        !d?.description_updated;
      const suffix = graphOnly ? " (graph updated)" : "";
      const wfLink = name
        ? (ctx?.workflowLink(name) ?? (
            <strong className="text-foreground">{name}</strong>
          ))
        : "a workflow";
      return (
        <>
          updated workflow {wfLink}
          {suffix}
        </>
      );
    },
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      if (e.eventDetails?.name_updated || e.eventDiffs?.name)
        chips.push({ label: "name \u2713", variant: "success" });
      if (e.eventDetails?.description_updated || e.eventDiffs?.description)
        chips.push({ label: "description \u2713", variant: "success" });
      if (e.eventDetails?.graph_updated)
        chips.push({ label: "graph \u2713", variant: "success" });
      return chips.length > 0 ? chips : null;
    },
  },
  "project-updated": {
    icon: Pencil,
    color: "text-orange-600",
    getLabel: (e) =>
      `updated project ${(e.eventDetails?.project_name as string) || ""}`,
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      const diffs = e.eventDiffs;
      if (diffs) {
        Object.keys(diffs).forEach((key) => {
          chips.push({ label: `${key} \u2713`, variant: "success" });
        });
      }
      return chips.length > 0 ? chips : null;
    },
  },
  "project-deleted": {
    icon: Trash2,
    color: "text-red-400",
    getLabel: (e) => {
      const name = (e.eventDetails?.projectName as string) || "";
      return name ? `deleted project ${name}` : "deleted project";
    },
  },
  "project-undeleted": {
    icon: Undo2,
    color: "text-green-500",
    getLabel: (e) => {
      const name = (e.eventDetails?.projectName as string) || "";
      return name ? `restored project ${name}` : "restored project";
    },
  },
  "project-purged": {
    icon: Flame,
    color: "text-red-600",
    getLabel: (e) => {
      const name = (e.eventDetails?.projectName as string) || "";
      return name
        ? `permanently deleted project ${name}`
        : "permanently deleted project";
    },
  },
  "workflow-deleted": {
    icon: Trash2,
    color: "text-red-400",
    getLabel: (e) => {
      const name = (e.eventDetails?.workflowName as string) || "";
      return name ? `deleted workflow ${name}` : "deleted workflow";
    },
  },
  "workflow-undeleted": {
    icon: Undo2,
    color: "text-green-500",
    getLabel: (e) => {
      const name = (e.eventDetails?.workflowName as string) || "";
      return name ? `restored workflow ${name}` : "restored workflow";
    },
  },
  "workflow-purged": {
    icon: Flame,
    color: "text-red-600",
    getLabel: (e) => {
      const name = (e.eventDetails?.workflowName as string) || "";
      return name
        ? `permanently deleted workflow ${name}`
        : "permanently deleted workflow";
    },
  },
  "workflow-duplicated": {
    icon: Copy,
    color: "text-blue-500",
    getLabel: (e) => {
      const name = (e.eventDetails?.workflowName as string) || "";
      return name ? `duplicated workflow ${name}` : "duplicated workflow";
    },
  },
  "workflow-version-deleted": {
    icon: Trash2,
    color: "text-red-400",
    getLabel: (e) => {
      const ver = e.eventDetails?.version_number;
      const wf = (e.eventDetails?.workflow_name as string) || "a workflow";
      return ver != null
        ? `deleted version ${ver} of ${wf}`
        : `deleted version of ${wf}`;
    },
  },
  "workflow-version-restored": {
    icon: RotateCcw,
    color: "text-green-500",
    getLabel: (e) => {
      const ver = e.eventDetails?.version_number;
      const wf = (e.eventDetails?.workflow_name as string) || "a workflow";
      return ver != null
        ? `restored version ${ver} of ${wf}`
        : `restored version of ${wf}`;
    },
  },
  "workflow-node-added": {
    icon: Plus,
    color: "text-green-600",
    getLabel: (e, ctx) => {
      const nodes =
        (e.eventDetails?.nodes_added as Array<{ node_label?: string }>) || [];
      const wfName = e.eventDetails?.workflow_name as string;
      const wf = wfName ? (
        <>
          workflow{" "}
          {ctx?.workflowLink(wfName) ?? (
            <strong className="text-foreground">{wfName}</strong>
          )}
        </>
      ) : (
        "a workflow"
      );
      // Handle flat single-node format from domain emission
      if (nodes.length === 0 && e.eventDetails?.node_type) {
        const label =
          (e.eventDetails?.node_label as string) ||
          (e.eventDetails?.node_type as string);
        return (
          <>
            added {label} to {wf}
          </>
        );
      }
      if (nodes.length === 0) return <>updated nodes in {wf}</>;
      if (nodes.length === 1)
        return (
          <>
            added {nodes[0]?.node_label || "a node"} to {wf}
          </>
        );
      return (
        <>
          added {nodes.length} nodes to {wf}
        </>
      );
    },
    getDetails: (e) => {
      const nodes =
        (e.eventDetails?.nodes_added as Array<{ node_type?: string }>) || [];
      // Handle flat single-node format from domain emission
      if (nodes.length === 0 && e.eventDetails?.node_type)
        return [{ label: `type: ${e.eventDetails.node_type as string}` }];
      if (nodes.length === 1 && nodes[0]?.node_type)
        return [{ label: `type: ${nodes[0].node_type}` }];
      return null;
    },
  },
  "workflow-node-removed": {
    icon: Minus,
    color: "text-red-500",
    getLabel: (e, ctx) => {
      const nodes =
        (e.eventDetails?.nodes_removed as Array<{ node_label?: string }>) || [];
      const wfName = e.eventDetails?.workflow_name as string;
      const wf = wfName ? (
        <>
          workflow{" "}
          {ctx?.workflowLink(wfName) ?? (
            <strong className="text-foreground">{wfName}</strong>
          )}
        </>
      ) : (
        "a workflow"
      );
      if (nodes.length === 0) return <>updated nodes in {wf}</>;
      if (nodes.length === 1)
        return (
          <>
            removed {nodes[0]?.node_label || "a node"} from {wf}
          </>
        );
      return (
        <>
          removed {nodes.length} nodes from {wf}
        </>
      );
    },
    getDetails: (e) => {
      const nodes =
        (e.eventDetails?.nodes_removed as Array<{ node_type?: string }>) || [];
      if (nodes.length === 1 && nodes[0]?.node_type)
        return [{ label: `type: ${nodes[0].node_type}` }];
      return null;
    },
  },
  "workflow-node-configured": {
    icon: Settings,
    color: "text-blue-500",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.workflow_name as string;
      const wf = name ? (
        <>
          workflow{" "}
          {ctx?.workflowLink(name) ?? (
            <strong className="text-foreground">{name}</strong>
          )}
        </>
      ) : (
        "a workflow"
      );
      return <>configured a node in {wf}</>;
    },
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      const nodeType = e.eventDetails?.node_type as string;
      if (nodeType) chips.push({ label: `type: ${nodeType}` });
      const diffs = e.eventDiffs;
      if (diffs) {
        const entries = Object.entries(diffs);
        const maxChips = 5;
        entries.slice(0, maxChips).forEach(([key, diff]) => {
          const d = diff as { old: unknown; new: unknown };
          const oldStr = formatDiffValue(d.old);
          const newStr = formatDiffValue(d.new);
          if (oldStr && newStr) {
            chips.push({ label: `${key}: ${oldStr} \u2192 ${newStr}` });
          } else if (newStr) {
            chips.push({ label: `${key}: ${newStr}`, variant: "success" });
          } else {
            chips.push({ label: `${key} \u2713`, variant: "success" });
          }
        });
        if (entries.length > maxChips) {
          chips.push({ label: `+${entries.length - maxChips} more` });
        }
      }
      return chips.length > 0 ? chips : null;
    },
  },
  "workflow-connection-added": {
    icon: LinkIcon,
    color: "text-blue-400",
    getLabel: (e, ctx) => {
      const wfName = e.eventDetails?.workflow_name as string;
      const wf = wfName ? (
        <>
          workflow{" "}
          {ctx?.workflowLink(wfName) ?? (
            <strong className="text-foreground">{wfName}</strong>
          )}
        </>
      ) : (
        "a workflow"
      );
      // Flat single-connection format from domain emission
      const srcLabel =
        (e.eventDetails?.source_node_label as string) ||
        (e.eventDetails?.source_node_type as string);
      const tgtLabel =
        (e.eventDetails?.target_node_label as string) ||
        (e.eventDetails?.target_node_type as string);
      if (srcLabel && tgtLabel) {
        return (
          <>
            connected {srcLabel} to {tgtLabel} in {wf}
          </>
        );
      }
      if (e.eventDetails?.source_node_id) {
        return <>added a connection in {wf}</>;
      }
      // Legacy batch format
      const edges =
        (e.eventDetails?.edges_added as Array<{
          source_node_label?: string;
          target_node_label?: string;
        }>) || [];
      if (edges.length === 1 && edges[0]?.source_node_label)
        return (
          <>
            connected {edges[0].source_node_label} to{" "}
            {edges[0].target_node_label}
          </>
        );
      return (
        <>
          added {edges.length} connection{edges.length !== 1 ? "s" : ""} in {wf}
        </>
      );
    },
    getDetails: (e) => {
      const srcType = e.eventDetails?.source_node_type as string;
      const tgtType = e.eventDetails?.target_node_type as string;
      if (srcType && tgtType)
        return [{ label: `${srcType} \u2192 ${tgtType}` }];
      return null;
    },
  },
  "workflow-connection-removed": {
    icon: Unlink,
    color: "text-red-400",
    getLabel: (e, ctx) => {
      const wfName = e.eventDetails?.workflow_name as string;
      const wf = wfName ? (
        <>
          workflow{" "}
          {ctx?.workflowLink(wfName) ?? (
            <strong className="text-foreground">{wfName}</strong>
          )}
        </>
      ) : (
        "a workflow"
      );
      // Flat single-connection format from domain emission
      const srcLabel =
        (e.eventDetails?.source_node_label as string) ||
        (e.eventDetails?.source_node_type as string);
      const tgtLabel =
        (e.eventDetails?.target_node_label as string) ||
        (e.eventDetails?.target_node_type as string);
      if (srcLabel && tgtLabel) {
        return (
          <>
            disconnected {srcLabel} from {tgtLabel} in {wf}
          </>
        );
      }
      if (e.eventDetails?.source_node_id) {
        return <>removed a connection from {wf}</>;
      }
      // Legacy batch format
      const edges =
        (e.eventDetails?.edges_removed as Array<{
          source_node_label?: string;
          target_node_label?: string;
        }>) || [];
      if (edges.length === 1 && edges[0]?.source_node_label)
        return (
          <>
            disconnected {edges[0].source_node_label} from{" "}
            {edges[0].target_node_label}
          </>
        );
      return (
        <>
          removed {edges.length} connection{edges.length !== 1 ? "s" : ""} from{" "}
          {wf}
        </>
      );
    },
    getDetails: (e) => {
      const srcType = e.eventDetails?.source_node_type as string;
      const tgtType = e.eventDetails?.target_node_type as string;
      if (srcType && tgtType)
        return [{ label: `${srcType} \u2192 ${tgtType}` }];
      return null;
    },
  },
  "timeline-created": {
    icon: Film,
    color: "text-purple-600",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.timeline_name as string;
      const pName = ctx?.getProjectName(e.projectId);
      const tl = name ? (
        (ctx?.timelineLink(name) ?? (
          <strong className="text-foreground">{name}</strong>
        ))
      ) : pName ? (
        <>
          {ctx?.timelineLink("timeline") ?? "timeline"} of{" "}
          <strong className="text-foreground">{pName}</strong>
        </>
      ) : (
        "a timeline"
      );
      return <>created {tl}</>;
    },
    getDetails: (e) => {
      const ratio = e.eventDetails?.aspect_ratio as string;
      return ratio ? [{ label: `aspect: ${ratio}` }] : null;
    },
  },
  "timeline-updated": {
    icon: Pencil,
    color: "text-purple-500",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.timeline_name as string;
      const pName = ctx?.getProjectName(e.projectId);
      const tl = name ? (
        (ctx?.timelineLink(name) ?? (
          <strong className="text-foreground">{name}</strong>
        ))
      ) : pName ? (
        <>
          {ctx?.timelineLink("timeline") ?? "timeline"} of{" "}
          <strong className="text-foreground">{pName}</strong>
        </>
      ) : (
        "a timeline"
      );
      return <>updated {tl}</>;
    },
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      if (e.eventDetails?.name_updated || e.eventDiffs?.name)
        chips.push({ label: "name \u2713", variant: "success" });
      if (e.eventDetails?.description_updated || e.eventDiffs?.description)
        chips.push({ label: "description \u2713", variant: "success" });
      if (e.eventDetails?.graph_updated)
        chips.push({ label: "graph \u2713", variant: "success" });
      return chips.length > 0 ? chips : null;
    },
  },
  "timeline-version-created": {
    icon: FilePlus,
    color: "text-blue-500",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.timeline_name as string;
      const pName = ctx?.getProjectName(e.projectId);
      const tl = name ? (
        (ctx?.timelineLink(name) ?? (
          <strong className="text-foreground">{name}</strong>
        ))
      ) : pName ? (
        <>
          {ctx?.timelineLink("timeline") ?? "timeline"} of{" "}
          <strong className="text-foreground">{pName}</strong>
        </>
      ) : (
        "a timeline"
      );
      return <>saved version of {tl}</>;
    },
    getDetails: (e) => {
      const ver = e.eventDetails?.version_number;
      return ver != null ? [{ label: `v${ver}` }] : null;
    },
  },
  "timeline-version-deleted": {
    icon: Trash2,
    color: "text-red-400",
    getLabel: (e, ctx) => {
      const name = e.eventDetails?.timeline_name as string;
      const pName = ctx?.getProjectName(e.projectId);
      const tl = name ? (
        (ctx?.timelineLink(name) ?? (
          <strong className="text-foreground">{name}</strong>
        ))
      ) : pName ? (
        <>
          {ctx?.timelineLink("timeline") ?? "timeline"} of{" "}
          <strong className="text-foreground">{pName}</strong>
        </>
      ) : (
        "a timeline"
      );
      return <>deleted version of {tl}</>;
    },
  },
  // ── Asset cleanup ──────────────────────────────────────────
  "asset-undeleted": {
    icon: Undo2,
    color: "text-green-500",
    getLabel: (e) => {
      const name = e.eventDetails?.file_name as string;
      return name ? (
        <span title={name}>restored an asset</span>
      ) : (
        "restored an asset"
      );
    },
    getDetails: statusTransitionChips,
  },
  "asset-purged": {
    icon: Flame,
    color: "text-red-600",
    getLabel: (e) => {
      const name = e.eventDetails?.file_name as string;
      return name ? (
        <span title={name}>permanently deleted an asset</span>
      ) : (
        "permanently deleted an asset"
      );
    },
  },
  "asset-updated": {
    icon: Pencil,
    color: "text-blue-500",
    getLabel: (e) => {
      const name = e.eventDetails?.file_name as string;
      return name ? (
        <span title={name}>updated asset</span>
      ) : (
        "updated an asset"
      );
    },
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      const diffs = e.eventDiffs;
      if (diffs) {
        Object.keys(diffs).forEach((key) => {
          chips.push({ label: `${key} \u2713`, variant: "success" });
        });
      }
      return chips.length > 0 ? chips : null;
    },
  },
  // ── Workflow version cleanup ───────────────────────────────
  "workflow-version-undeleted": {
    icon: Undo2,
    color: "text-green-500",
    getLabel: (e) => {
      const ver = e.eventDetails?.version_number;
      const wf = (e.eventDetails?.workflow_name as string) || "a workflow";
      return ver != null
        ? `restored version ${ver} of ${wf}`
        : `restored version of ${wf}`;
    },
    getDetails: statusTransitionChips,
  },
  "workflow-version-purged": {
    icon: Flame,
    color: "text-red-600",
    getLabel: (e) => {
      const ver = e.eventDetails?.version_number;
      const wf = (e.eventDetails?.workflow_name as string) || "a workflow";
      return ver != null
        ? `permanently deleted version ${ver} of ${wf}`
        : `permanently deleted version of ${wf}`;
    },
  },
  // ── Organisation lifecycle ─────────────────────────────────
  "organisation-created": {
    icon: Building2,
    color: "text-blue-600",
    getLabel: (e) =>
      `created organisation ${(e.eventDetails?.organisation_name as string) || ""}`,
  },
  "organisation-updated": {
    icon: Pencil,
    color: "text-orange-500",
    getLabel: (e) =>
      `updated organisation ${(e.eventDetails?.organisation_name as string) || ""}`,
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      const diffs = e.eventDiffs;
      if (diffs) {
        Object.keys(diffs).forEach((key) => {
          chips.push({ label: `${key} \u2713`, variant: "success" });
        });
      }
      return chips.length > 0 ? chips : null;
    },
  },
  "organisation-suspended": {
    icon: PauseCircle,
    color: "text-yellow-600",
    getLabel: (e) =>
      `suspended organisation ${(e.eventDetails?.organisation_name as string) || ""}`,
    getDetails: () => [{ label: "suspended", variant: "warning" }],
  },
  "organisation-restored": {
    icon: Undo2,
    color: "text-green-600",
    getLabel: (e) =>
      `restored organisation ${(e.eventDetails?.organisation_name as string) || ""}`,
    getDetails: statusTransitionChips,
  },
  "organisation-deleted": {
    icon: Trash2,
    color: "text-red-600",
    getLabel: (e) =>
      `deleted organisation ${(e.eventDetails?.organisation_name as string) || ""}`,
    getDetails: () => [{ label: "deleted", variant: "error" }],
  },
  // ── Organisation membership ────────────────────────────────
  "organisation-member-invited": {
    icon: Mail,
    color: "text-blue-500",
    getLabel: (e) => {
      const target =
        (e.eventDetails?.target_user_email as string) || "a member";
      return `invited ${target} to the organisation`;
    },
  },
  "organisation-member-joined": {
    icon: UserPlus,
    color: "text-green-600",
    getLabel: (e) => {
      const target = (e.eventDetails?.target_user_name as string) || "a member";
      return `${target} joined the organisation`;
    },
  },
  "organisation-member-role-changed": {
    icon: ShieldCheck,
    color: "text-blue-500",
    getLabel: (e) => {
      const target = (e.eventDetails?.target_user_name as string) || "a member";
      return `changed role for ${target}`;
    },
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      const oldRole = e.eventDiffs?.role_id?.old as string | undefined;
      const newRole = e.eventDiffs?.role_id?.new as string | undefined;
      if (oldRole && newRole)
        chips.push({ label: `${oldRole} \u2192 ${newRole}` });
      return chips.length > 0 ? chips : null;
    },
  },
  "organisation-member-suspended": {
    icon: PauseCircle,
    color: "text-yellow-600",
    getLabel: (e) => {
      const target = (e.eventDetails?.target_user_name as string) || "a member";
      return `suspended ${target}`;
    },
    getDetails: statusTransitionChips,
  },
  "organisation-member-removed": {
    icon: UserMinus,
    color: "text-red-500",
    getLabel: (e) => {
      const target = (e.eventDetails?.target_user_name as string) || "a member";
      return `removed ${target} from the organisation`;
    },
    getDetails: statusTransitionChips,
  },
  // ── Organisation roles & permissions ───────────────────────
  "organisation-role-created": {
    icon: ShieldPlus,
    color: "text-green-600",
    getLabel: (e) =>
      `created role ${((e.eventDetails?.role_tk ?? e.eventDetails?.role_name) as string) || ""}`,
  },
  "organisation-role-updated": {
    icon: ShieldCheck,
    color: "text-orange-500",
    getLabel: (e) =>
      `updated role ${((e.eventDetails?.role_tk ?? e.eventDetails?.role_name) as string) || ""}`,
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      const diffs = e.eventDiffs;
      if (diffs) {
        Object.keys(diffs).forEach((key) => {
          chips.push({ label: `${key} \u2713`, variant: "success" });
        });
      }
      return chips.length > 0 ? chips : null;
    },
  },
  "organisation-role-deleted": {
    icon: ShieldX,
    color: "text-red-500",
    getLabel: (e) =>
      `deleted role ${((e.eventDetails?.role_tk ?? e.eventDetails?.role_name) as string) || ""}`,
  },
  "organisation-role-permissions-updated": {
    icon: KeyRound,
    color: "text-purple-500",
    getLabel: (e) =>
      `updated permissions for ${((e.eventDetails?.role_tk ?? e.eventDetails?.role_name) as string) || "a role"}`,
    getDetails: (e) => {
      const added = e.eventDetails?.permissions_added as number | undefined;
      const removed = e.eventDetails?.permissions_removed as number | undefined;
      const chips: DetailChip[] = [];
      if (added) chips.push({ label: `+${added}`, variant: "success" });
      if (removed) chips.push({ label: `-${removed}`, variant: "error" });
      return chips.length > 0 ? chips : null;
    },
  },
  // ── SSO & SCIM ─────────────────────────────────────────────
  "organisation-sso-config-updated": {
    icon: KeyRound,
    color: "text-blue-600",
    getLabel: () => `updated SSO configuration`,
    getDetails: (e) => {
      const provider = e.eventDetails?.sso_provider as string;
      return provider ? [{ label: provider }] : null;
    },
  },
  // ── Organisation sharing ──────────────────────────────────
  "organisation-sharing-domain-added": {
    icon: Globe,
    color: "text-green-600",
    getLabel: (e) => {
      const domain = e.eventDetails?.domain as string;
      return domain
        ? `added sharing domain ${domain}`
        : "added a sharing domain";
    },
    getDetails: (e) => {
      const domain = e.eventDetails?.domain as string;
      return domain ? [{ label: domain }] : null;
    },
  },
  "organisation-sharing-domain-removed": {
    icon: Globe,
    color: "text-red-500",
    getLabel: (e) => {
      const domain = e.eventDetails?.domain as string;
      return domain
        ? `removed sharing domain ${domain}`
        : "removed a sharing domain";
    },
    getDetails: (e) => {
      const domain = e.eventDetails?.domain as string;
      return domain ? [{ label: domain, variant: "error" }] : null;
    },
  },
  "organisation-sharing-restriction-updated": {
    icon: Shield,
    color: "text-amber-500",
    getLabel: (e) => {
      const restriction = e.eventDetails?.restriction as string;
      return restriction
        ? `updated sharing restriction to ${restriction}`
        : "updated sharing restriction";
    },
    getDetails: (e) => {
      const chips: DetailChip[] = [];
      const diffs = e.eventDiffs;
      if (diffs) {
        Object.keys(diffs).forEach((key) => {
          const d = diffs[key] as { old: unknown; new: unknown };
          chips.push({
            label: `${key}: ${String(d.old)} \u2192 ${String(d.new)}`,
          });
        });
      }
      return chips.length > 0 ? chips : null;
    },
  },
  "scim-user-provisioned": {
    icon: Server,
    color: "text-green-600",
    getLabel: (e) => {
      const target = (e.eventDetails?.target_user_email as string) || "a user";
      return `provisioned ${target} via SCIM`;
    },
  },
  "scim-user-deprovisioned": {
    icon: Server,
    color: "text-red-500",
    getLabel: (e) => {
      const target = (e.eventDetails?.target_user_email as string) || "a user";
      return `deprovisioned ${target} via SCIM`;
    },
  },
};

const defaultEventConfig: EventConfig = {
  icon: Activity,
  color: "text-gray-500",
  getLabel: (e) => e.eventType.slug,
};

// ============================================================
// Helpers
// ============================================================

function formatRelativeTime(isoDate: string): string {
  const dt = DateTime.fromISO(isoDate);
  const diff = dt.diffNow(["minutes", "hours", "days"]);

  const mins = Math.abs(Math.round(diff.minutes));
  const hours = Math.abs(Math.round(diff.hours));
  const days = Math.abs(Math.round(diff.days));

  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return dt.toFormat("MMM d");
}

function getActorDisplay(actor: ActivityEvent["actor"]): string {
  if (!actor) return "System";
  return actor.displayName;
}

/** Format a diff value for display in chips (handles null, string, number, boolean). */
function formatDiffValue(val: unknown): string | null {
  if (val === null || val === undefined) return null;
  if (typeof val === "boolean") return val ? "true" : "false";
  if (typeof val === "number") return String(val);
  if (typeof val === "string") {
    if (val.length === 0) return '""';
    if (val.length > 60) return val.slice(0, 60) + "\u2026";
    return val;
  }
  return "[object]";
}

// ============================================================
// Entity Link Component
// ============================================================

const EntityLink: React.FC<{
  children: React.ReactNode;
  entityType: "workflow" | "project" | "timeline" | "asset";
  entityName?: string;
  onNavigate: () => void;
}> = ({ children, entityType, entityName, onNavigate }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        className="text-foreground/80 hover:text-foreground underline decoration-dotted underline-offset-2 hover:decoration-solid transition-colors"
      >
        {children}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="min-w-[140px]">
      <DropdownMenuItem onSelect={onNavigate}>
        <ExternalLink className="h-3.5 w-3.5 mr-2" />
        Go to {entityName || entityType}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

// ============================================================
// Empty State Messages
// ============================================================

const CATEGORY_EMPTY_MESSAGES: Record<
  ActivityEventCategory,
  { message: string; cta?: string }
> = {
  all: {
    message: "No activity yet. Create a workflow to get started.",
  },
  workflows: {
    message: "No workflow activity in the last 30 days.",
    cta: "Clear filter",
  },
  assets: {
    message: "No asset activity in the last 30 days.",
    cta: "Clear filter",
  },
  timelines: {
    message: "No timeline activity in the last 30 days.",
    cta: "Clear filter",
  },
  team: {
    message: "No team activity in the last 30 days.",
    cta: "Clear filter",
  },
  organisations: {
    message: "No organisation activity in the last 30 days.",
    cta: "Clear filter",
  },
};

// ============================================================
// Component
// ============================================================

interface ActivityFeedProps {
  scope: { projectId: string } | { teamId: string } | "user";
  className?: string;
  eventTypes?: ActivityEventSlug[];
  activeCategory?: ActivityEventCategory;
  onClearFilter?: () => void;
  onClose?: () => void;
  open?: boolean;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  scope,
  className,
  eventTypes,
  activeCategory = "all",
  onClearFilter,
  onClose,
  open,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { projects } = useAppStore();

  // Build projectId → projectName lookup from store for fallback labels
  const projectNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects) {
      if (p.id && p.name) map.set(p.id, p.name);
    }
    return map;
  }, [projects]);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useActivityFeedInfinite(scope, { eventTypes });

  // Invalidate on mount (Sheet unmounts content when closed, so each
  // open is a fresh mount) and when open transitions from false to true.
  const didInvalidate = useRef(false);
  useEffect(() => {
    if (open && !didInvalidate.current) {
      didInvalidate.current = true;
      void queryClient.invalidateQueries({ queryKey: ["activity-feed"] });
    }
    if (!open) didInvalidate.current = false;
  }, [open, queryClient]);

  const { events, deletedAssetIds } = useMemo(() => {
    if (!data?.pages)
      return {
        events: [] as ActivityEvent[],
        deletedAssetIds: new Set<string>(),
      };
    const all = data.pages.flatMap((page) => page.data ?? []);

    // Collect asset IDs that have been deleted — these should not be previewable.
    const deletedAssetIds = new Set<string>();
    for (const e of all) {
      if (e.eventType.slug === "asset-deleted" && e.assetId) {
        deletedAssetIds.add(e.assetId);
      }
    }

    // Collect timestamps that have specific graph-change events
    const graphEventSlugs = new Set([
      "workflow-node-added",
      "workflow-node-removed",
      "workflow-node-configured",
      "workflow-connection-added",
      "workflow-connection-removed",
    ]);
    const timestampsWithGraphEvents = new Set(
      all
        .filter((e) => graphEventSlugs.has(e.eventType.slug))
        .map((e) => e.createdAt),
    );

    // Collect timeline IDs that have a version-created event so we can hide
    // the paired timeline-updated (saving a version always emits both).
    const timelinesWithVersionEvent = new Set<string>();
    for (const e of all) {
      if (e.eventType.slug === "timeline-version-created") {
        const tlId = e.eventDetails?.timeline_id as string | undefined;
        if (tlId) timelinesWithVersionEvent.add(tlId);
      }
    }

    // Deduplicate: collapse consecutive events with the same slug and key detail
    const seen = new Set<string>();

    const filtered = all.filter((e) => {
      // Hide graph-only workflow-metadata-updated when specific node/connection
      // events already describe the same save (same timestamp)
      if (e.eventType.slug === "workflow-metadata-updated") {
        const d = e.eventDiffs as Record<string, unknown> | null;
        if (
          d?.graph_updated === true &&
          !d?.name_updated &&
          !d?.description_updated &&
          timestampsWithGraphEvents.has(e.createdAt)
        ) {
          return false;
        }
      }

      // Hide timeline-updated when a timeline-version-created exists for the same
      // timeline — saving a version always emits both, so keep only the version event.
      if (e.eventType.slug === "timeline-updated") {
        const tlId = e.eventDetails?.timeline_id as string | undefined;
        if (tlId && timelinesWithVersionEvent.has(tlId)) return false;
      }

      // Deduplicate asset events: keep only the first (most recent) event per asset.
      // This collapses asset-created + asset-confirmed into a single entry.
      if (
        e.eventType.slug === "asset-created" ||
        e.eventType.slug === "asset-confirmed"
      ) {
        const assetKey = e.assetId ?? (e.eventDetails?.file_name as string);
        if (assetKey) {
          const key = `asset:${assetKey}`;
          if (seen.has(key)) return false;
          seen.add(key);
        }
      }

      return true;
    });

    return { events: filtered, deletedAssetIds };
  }, [data]);

  // Navigation helpers — close drawer first, then navigate
  const navigateToWorkflow = (workflowId: string) => {
    onClose?.();
    navigate(routes.workflow(workflowId));
  };
  const navigateToProject = (pid: string) => {
    onClose?.();
    navigate(routes.production(pid));
  };
  const navigateToTimeline = (pid: string) => {
    onClose?.();
    navigate(routes.timeline(pid));
  };

  // Asset preview state
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const openAssetPreview = async (assetId: string) => {
    try {
      const result = await getAsset({
        path: { assetId },
        throwOnError: true,
      });
      const asset = result?.data?.data as Asset;
      if (asset) {
        setPreviewAsset(asset);
        setIsPreviewOpen(true);
      } else {
        toast.error(
          "This resource is unavailable. You may not have enough permissions or the resource has been deleted.",
        );
      }
    } catch {
      toast.error(
        "This resource is unavailable. You may not have enough permissions or the resource has been deleted.",
      );
    }
  };

  if (isLoading) {
    return (
      <div className={cn("space-y-3 px-6 py-4", className)}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-3 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-muted rounded w-3/4" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center px-6 py-6 text-center",
          className,
        )}
      >
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <p className="text-sm text-muted-foreground mb-2">
          Failed to load activity
        </p>
        <button
          onClick={() => refetch()}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          Tap to retry
        </button>
      </div>
    );
  }

  if (events.length === 0) {
    const empty = CATEGORY_EMPTY_MESSAGES[activeCategory];
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center px-6 py-6 text-center",
          className,
        )}
      >
        <Activity className="h-8 w-8 text-muted-foreground/50 mb-2" />
        <p className="text-sm text-muted-foreground">{empty.message}</p>
        {empty.cta && onClearFilter && (
          <button
            onClick={onClearFilter}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 mt-2"
          >
            {empty.cta}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col overflow-hidden", className)}>
      <ScrollArea className="flex-1">
        <div className="space-y-1 px-6 py-2">
          {events.map((event) => {
            const config =
              eventConfig[event.eventType.slug] ?? defaultEventConfig;
            const Icon = config.icon;
            const details = config.getDetails?.(event) ?? null;
            const isSystem = !event.actor;

            // Determine if this event has a navigable workflow/project
            const workflowId = event.workflowId;
            const eventProjectId = event.projectId;

            // Build label context with inline entity links
            const labelCtx: LabelContext = {
              workflowLink: (name) =>
                workflowId ? (
                  <EntityLink
                    entityType="workflow"
                    entityName={name}
                    onNavigate={() => navigateToWorkflow(workflowId)}
                  >
                    <strong>{name}</strong>
                  </EntityLink>
                ) : (
                  <strong className="text-foreground">{name}</strong>
                ),
              projectLink: (name) =>
                eventProjectId ? (
                  <EntityLink
                    entityType="project"
                    entityName={name}
                    onNavigate={() => navigateToProject(eventProjectId)}
                  >
                    <strong>{name}</strong>
                  </EntityLink>
                ) : (
                  <strong className="text-foreground">{name}</strong>
                ),
              assetLink: (name) =>
                event.assetId && !deletedAssetIds.has(event.assetId) ? (
                  <EntityLink
                    entityType="asset"
                    entityName={name}
                    onNavigate={() => openAssetPreview(event.assetId!)}
                  >
                    <strong>{name}</strong>
                  </EntityLink>
                ) : (
                  <strong className="text-foreground">{name}</strong>
                ),
              timelineLink: (name) =>
                eventProjectId ? (
                  <EntityLink
                    entityType="timeline"
                    entityName={name}
                    onNavigate={() => navigateToTimeline(eventProjectId)}
                  >
                    <strong>{name}</strong>
                  </EntityLink>
                ) : (
                  <strong className="text-foreground">{name}</strong>
                ),
              getProjectName: (pid) =>
                pid ? projectNameMap.get(pid) : undefined,
            };

            return (
              <div
                key={event.id}
                className="group flex items-start gap-3 p-3 rounded border border-white/10 hover:bg-accent/50 transition-colors"
              >
                <div
                  className={cn(
                    "mt-0.5 flex-shrink-0 w-7 h-7 rounded flex items-center justify-center bg-muted/60",
                    config.color,
                  )}
                >
                  {isSystem ? (
                    <Bot className="h-3.5 w-3.5" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-snug">
                    <span className="font-medium text-foreground">
                      {getActorDisplay(event.actor)}
                    </span>{" "}
                    <span className="text-muted-foreground">
                      {config.getLabel(event, labelCtx)}
                    </span>
                  </p>

                  {/* Detail chips and time on same row */}
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {details && details.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {details.map((chip, i) => (
                          <span
                            key={i}
                            className={cn(
                              "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none",
                              chipVariantClasses[chip.variant ?? "default"],
                            )}
                          >
                            {chip.label}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className="text-xs text-muted-foreground/70">
                      {formatRelativeTime(event.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {hasNextPage && (
            <div className="flex justify-center py-2">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 disabled:opacity-50"
              >
                {isFetchingNextPage ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  "Load more"
                )}
              </button>
            </div>
          )}
        </div>
      </ScrollArea>

      <AssetPreviewModal
        asset={previewAsset}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewAsset(null);
        }}
      />
    </div>
  );
};
